/**
 * The two-level taxonomy rail as the administration area edits it (TASK-0052).
 *
 * Four conventions of the surface are encoded here rather than left to the screens:
 *
 * - **a row is addressed by its derived document identifier** (`cat_backend`, `sub_backend_rest-api`) and not by a GUID,
 *   because the identifier is the business key the stored prompts and templates carry. It is derived from the slug and
 *   never moves, so a rename cannot break a stored reference;
 * - **a subcategory is always addressed beneath its parent.** Every subcategory route is nested under
 *   `categories/{categoryId}/`, and the API answers `404` for a row that does not sit beneath the routed parent. The
 *   client sends the parent it read the row from, so a stale tab cannot edit a sibling's child;
 * - **deleting is soft and restoring is a second decision.** `DELETE` keeps the row so references still resolve; the
 *   restore route brings it back *disabled*, so re-offering it in a picker is a deliberate `state` write;
 * - **a category is refused while it holds a live subcategory** (`409 concurrency_conflict`), because nothing is
 *   cascaded. The child count on the row is what lets a screen explain the refusal before it is attempted.
 *
 * @see TASK-0052 — the taxonomy contract and the lifecycle rules above.
 */

import { apiClient } from '@/lib/apiClient';

/**
 * One category as the administration panel reads it (`CategoryDto`).
 *
 * The instants are the strings the API serialises; nothing in this module parses them.
 */
export interface AdminCategory {
  /** Document identifier derived from the slug, such as `cat_backend`. */
  id: string;
  /** Immutable business key; a rename never changes it. */
  slug: string;
  /** Persian display title. */
  nameFa: string;
  /** English display title. */
  nameEn: string;
  /** Icon name the SPA renders, or `null`. */
  iconName: string | null;
  /** Presentation order; lower sorts first. */
  order: number;
  /** Whether the category is offered in pickers. */
  isActive: boolean;
  /** Whether the category was soft-deleted. */
  isDeleted: boolean;
  /** Number of subcategories that were not soft-deleted; the reason a deletion can be refused. */
  subCategoryCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  deletedAtUtc: string | null;
}

/** One subcategory as the administration panel reads it (`SubCategoryDto`). */
export interface AdminSubCategory {
  /** Document identifier such as `sub_backend_rest-api`. */
  id: string;
  /** Identifier of the parent category, fixed at creation. */
  categoryId: string;
  /** Immutable slug, unique within the parent. */
  slug: string;
  /** Persian display title. */
  nameFa: string;
  /** English display title. */
  nameEn: string;
  /** Presentation order inside the parent. */
  order: number;
  /** Whether the subcategory is offered in pickers. */
  isActive: boolean;
  /** Whether the subcategory was soft-deleted. */
  isDeleted: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
  deletedAtUtc: string | null;
}

/** One category together with the subcategories beneath it (`CategoryDetailDto`). */
export interface AdminCategoryDetail {
  category: AdminCategory;
  subCategories: AdminSubCategory[];
}

/** Body of `POST /admin/categories`; the slug is accepted once, at creation only. */
export interface CategoryCreatePayload {
  slug: string;
  nameFa: string;
  nameEn: string;
  order: number;
  iconName: string | null;
  isActive: boolean;
}

/** Body of `PUT /admin/categories/{id}`; there is no slug member, because the slug must not move. */
export interface CategoryUpdatePayload {
  nameFa: string;
  nameEn: string;
  order: number;
  iconName: string | null;
}

/** Body of `POST /admin/categories/{categoryId}/subcategories`. */
export interface SubCategoryCreatePayload {
  slug: string;
  nameFa: string;
  nameEn: string;
  order: number;
  isActive: boolean;
}

/** Body of `PUT /admin/categories/{categoryId}/subcategories/{id}`. */
export interface SubCategoryUpdatePayload {
  nameFa: string;
  nameEn: string;
  order: number;
}

/** List flags the two collection routes accept; each route falls back to the row's default. */
export interface TaxonomyListFlags {
  /** Return soft-deleted rows as well. */
  includeDeleted?: boolean;
  /** Return disabled rows as well; the category list ignores it, the subcategory list accepts it. */
  includeInactive?: boolean;
}

/** Administrative category operations; every call is role-gated on the API side. */
export const adminCategoryApi = {
  /**
   * Lists the category rail in presentation order.
   *
   * @param flags Whether soft-deleted rows are included; the panel passes `true` so it can restore one.
   * @returns Categories with their live subcategory counts.
   */
  async list(flags: TaxonomyListFlags = {}): Promise<AdminCategory[]> {
    const { data } = await apiClient.get<AdminCategory[]>('/admin/categories', {
      params: { includeDeleted: flags.includeDeleted ?? false },
    });

    return data;
  },

  /**
   * Reads one category together with its subcategories.
   *
   * @param categoryId Category identifier such as `cat_backend`.
   * @param flags Whether soft-deleted subcategories are included.
   * @returns The category and its subcategories in presentation order.
   */
  async get(categoryId: string, flags: TaxonomyListFlags = {}): Promise<AdminCategoryDetail> {
    const { data } = await apiClient.get<AdminCategoryDetail>(`/admin/categories/${categoryId}`, {
      params: { includeDeleted: flags.includeDeleted ?? false },
    });

    return data;
  },

  /** Creates a category and returns it as the server stored it. */
  async create(payload: CategoryCreatePayload): Promise<AdminCategory> {
    const { data } = await apiClient.post<AdminCategory>('/admin/categories', payload);

    return data;
  },

  /** Renames, reorders or re-icons a category; the slug and the identifier are untouched. */
  async update(categoryId: string, payload: CategoryUpdatePayload): Promise<AdminCategory> {
    const { data } = await apiClient.put<AdminCategory>(`/admin/categories/${categoryId}`, payload);

    return data;
  },

  /** Enables or disables a category, which decides whether pickers offer it. */
  async setState(categoryId: string, isActive: boolean): Promise<AdminCategory> {
    const { data } = await apiClient.post<AdminCategory>(`/admin/categories/${categoryId}/state`, {
      isActive,
    });

    return data;
  },

  /**
   * Soft-deletes a category.
   *
   * @param categoryId Category identifier.
   * @returns The category as it stands after the deletion.
   * @throws ApiError carrying `concurrency_conflict` when a live subcategory still sits beneath it.
   */
  async remove(categoryId: string): Promise<AdminCategory> {
    const { data } = await apiClient.delete<AdminCategory>(`/admin/categories/${categoryId}`);

    return data;
  },

  /** Brings a soft-deleted category back, **disabled**. */
  async restore(categoryId: string): Promise<AdminCategory> {
    const { data } = await apiClient.post<AdminCategory>(`/admin/categories/${categoryId}/restore`);

    return data;
  },
};

/** Administrative subcategory operations; every call names its parent, because no subcategory exists without one. */
export const adminSubCategoryApi = {
  /** Lists the subcategories of a parent, in presentation order. */
  async list(categoryId: string, flags: TaxonomyListFlags = {}): Promise<AdminSubCategory[]> {
    const { data } = await apiClient.get<AdminSubCategory[]>(
      `/admin/categories/${categoryId}/subcategories`,
      {
        params: {
          includeInactive: flags.includeInactive ?? false,
          includeDeleted: flags.includeDeleted ?? false,
        },
      },
    );

    return data;
  },

  /** Creates a subcategory beneath its parent. */
  async create(categoryId: string, payload: SubCategoryCreatePayload): Promise<AdminSubCategory> {
    const { data } = await apiClient.post<AdminSubCategory>(
      `/admin/categories/${categoryId}/subcategories`,
      payload,
    );

    return data;
  },

  /** Renames or reorders a subcategory; its slug, identifier and parent are untouched. */
  async update(
    categoryId: string,
    subCategoryId: string,
    payload: SubCategoryUpdatePayload,
  ): Promise<AdminSubCategory> {
    const { data } = await apiClient.put<AdminSubCategory>(
      `/admin/categories/${categoryId}/subcategories/${subCategoryId}`,
      payload,
    );

    return data;
  },

  /** Enables or disables a subcategory. */
  async setState(
    categoryId: string,
    subCategoryId: string,
    isActive: boolean,
  ): Promise<AdminSubCategory> {
    const { data } = await apiClient.post<AdminSubCategory>(
      `/admin/categories/${categoryId}/subcategories/${subCategoryId}/state`,
      { isActive },
    );

    return data;
  },

  /** Soft-deletes a subcategory; a later create of the same slug asks the administrator to restore it first. */
  async remove(categoryId: string, subCategoryId: string): Promise<AdminSubCategory> {
    const { data } = await apiClient.delete<AdminSubCategory>(
      `/admin/categories/${categoryId}/subcategories/${subCategoryId}`,
    );

    return data;
  },

  /** Brings a soft-deleted subcategory back, **disabled**. */
  async restore(categoryId: string, subCategoryId: string): Promise<AdminSubCategory> {
    const { data } = await apiClient.post<AdminSubCategory>(
      `/admin/categories/${categoryId}/subcategories/${subCategoryId}/restore`,
    );

    return data;
  },
};
